/**
 * KarroFin Admin — canonical constants.
 *
 * Every string here is copied verbatim from the backend so that swapping the
 * mock layer for real endpoints needs no translation step. Sources:
 *   cashpo-server/keys.py, cashpo-server/choices.py,
 *   cashpo-server/loan/models.py, cashpo-server/users/models.py
 *
 * Do not "tidy" these values -- the casing is inconsistent server-side on
 * purpose (e.g. funnel_status uses lowercase "approved"/"disbursed" while
 * precheck_status uses "Approved By NBFC").
 */

/* ---------------------------------------------------------------- products */
// Offers.product_type / LoanData.product_type
//
// Credit Card was retired as a product: it is gone from every filter, from the
// offer form. The backend still knows the string
// (and the card-only OFFER_LOAN_STATUS values below are kept for that reason),
// so a historical record still renders a readable status -- but nothing in this
// admin offers it as a choice any more.
export const PRODUCT_TYPE = {
  PERSONAL_LOAN: "Personal Loan",
  BUSINESS_LOAN: "Business Loan",
};

export const PRODUCT_TYPES = [
  PRODUCT_TYPE.PERSONAL_LOAN,
  PRODUCT_TYPE.BUSINESS_LOAN,
];

/**
 * How many products may be marked "recommended" at once, PER product type and
 * counting only ENABLED ones.
 *
 * A mirror of MAX_RECOMMENDED_PER_TYPE in cashpo-server/products/views.py,
 * which is the authority -- the serializer rejects a write past the cap
 * whatever the panel believes. This copy only decides when to grey a switch out
 * so the limit is visible before it is hit. Change both together.
 */
export const MAX_RECOMMENDED_PER_TYPE = 5;

// The product filter shown on most screens. "All" is a UI-only value and is
// never sent to the backend. (Offers is the exception: it switches between the
// two products outright, with no "All" view.)
export const PRODUCT_FILTER_ALL = "All";

/* ------------------------------------------------------------ offer status */
// loan/models.py :: Offers.OfferStatusChoice
export const OFFER_STATUS = {
  APPROVED: "Approved",
  REJECTED: "Rejected",
  PENDING: "Pending",
  USER_EXISTS: "User Exists",
};

// choices.py :: OFFER_LOAN_STATUS  (Offers.loan_status)
export const OFFER_LOAN_STATUS = {
  APPLICATION_PENDING: "Application Pending",
  ACTION_REQUIRED: "Action Required",
  APPROVED: "Approved",
  DISBURSED: "Disbursed",
  COMPLETED: "Completed",
  REJECTED: "Rejected",
  // Card-only statuses. Credit Card is no longer sold, but these stay so an
  // old record coming back from the backend still renders a labelled chip
  // instead of a blank one.
  CARD_DISPATCHED: "Card Dispatched",
  CARD_ACTIVATED: "Card Activated",
};

/* --------------------------------------------------- dashboard ratio views */
/**
 * The two halves of the dashboard's conversion-ratio toggle.
 *
 * Offer first because that is the wider population, Lead second and
 * PRESELECTED -- the lead ratios are the ones ops act on, the offer ratios are
 * the context for them.
 */
export const RATIO_VIEWS = [
  { value: "offer", label: "Offer" },
  { value: "lead", label: "Lead" },
];

/* ------------------------------------------------------ application status */
// loan/models.py :: LoanData.PreCheckChoice
export const PRECHECK_STATUS = {
  APPLIED: "Precheck Applied",
  PENDING: "Precheck Pending",
  APPROVED: "Precheck Approved",
  REJECTED_BY_CASHPO: "Rejected By Cashpo",
  REJECTED_BY_KARROFIN: "Rejected By Karrofin",
  REJECTED_BY_NBFC: "Rejected By NBFC",
  PENDING_BY_NBFC: "Pending By NBFC",
  DISBURSED_BY_NBFC: "Disbursed By NBFC",
  COMPLETED_BY_NBFC: "Completed By NBFC",
  APPROVED_BY_NBFC: "Approved By NBFC",
  OFFER_GENERATED: "Offer Generated",
  UNDER_PROCESS: "Under Process",
};

/* ---------------------------------------------------------- profile status */
// choices.py :: PROFILE_STATUS_V2  (UserProfileData.profile_status)
export const PROFILE_STATUS = {
  INITIAL: "Initial",
  EC_STAGE_1: "Check Eligibility Stage1",
  EC_STAGE_2: "Check Eligibility Stage2",
  LOAN_APPLICATION: "Loan Application",
  EMPLOYMENT_DETAILS: "Employment Details",
  GENERAL_INFO: "General Info",
  ADDRESS_DETAILS: "Address Details",
  RENT_ADDRESS_PROOF: "Rent Address Proof",
  BANK_STATEMENT: "Bank Statement",
  KYC: "KYC",
  PROFILE_COMPLETED: "Profile Completed",
  EC_REJECTED: "EC Rejected",
  LOAN_CREATED: "Loan Created",
};

// webpanel/apis.py :: CustomerListView.STATUS_MAPPING
// EC1/EC2 are admin-panel tab groupings, NOT stored values.
export const EC1_PROFILE_STATUSES = [
  PROFILE_STATUS.INITIAL,
  PROFILE_STATUS.EC_STAGE_1,
];

/* ----------------------------------------------------------- funnel status */
// users/models.py :: UserProfileData.FunnelStatus  (note the lowercase tail)
export const FUNNEL_STATUS = {
  LEAD: "Lead",
  INTERNAL_EC: "Internal EC",
  APPROVED: "approved",
  DISBURSED: "disbursed",
};

/* ------------------------------------------------------------- lead buckets */
// The buckets named in the KarroFin scope. A user may appear in more than one.
export const LEAD_BUCKET = {
  NO_OFFER_CLICKED: "No offer clicked",
  SOME_OFFERS_CLICKED: "Some offers clicked",
  ALL_OFFERS_CLICKED: "All offers clicked",
  EC1_PENDING: "EC1 Pending",
  UNDER_PROCESS: "Under Process",
  DISBURSED: "Disbursed",
  REJECTED: "Rejected",
};

export const LEAD_BUCKETS = [
  LEAD_BUCKET.NO_OFFER_CLICKED,
  LEAD_BUCKET.SOME_OFFERS_CLICKED,
  LEAD_BUCKET.ALL_OFFERS_CLICKED,
  LEAD_BUCKET.EC1_PENDING,
  LEAD_BUCKET.UNDER_PROCESS,
  LEAD_BUCKET.DISBURSED,
  LEAD_BUCKET.REJECTED,
];

/* ----------------------------------------------------- application pipeline */
// The offer/application progression named in the scope.
//
// OFFER_AVAILABLE describes a single OFFER: it exists and is open. It is still
// the right label on an offer row, but it is no longer a pipeline stage -- at
// customer level "has offers" is not one state but three, depending on how
// many of them the customer actually clicked.
export const APPLICATION_STAGE = {
  OFFER_AVAILABLE: "Offer Available",
  EC1_PENDING: "EC1 Pending",
  NO_OFFER_CLICKED: "No offer clicked",
  SOME_OFFERS_CLICKED: "Some offers clicked",
  ALL_OFFERS_CLICKED: "All offers clicked",
  UNDER_PROCESS: "Under Process",
  DISBURSED: "Disbursed",
  REJECTED: "Rejected",
};

// Pipeline order, as rendered on the Offer Management screen. These are
// CUSTOMER states, and the tiles count customers, because the middle three
// cannot be answered about a single offer -- "some offers clicked" is a fact
// about a person's whole set. Deliberately the same strings as the Leads
// buckets, so the two screens name the same state the same way and the
// existing STATUS_COLOR entries apply.
//
// EC1 comes first because it is the earlier state: a user is EC1 Pending
// before any offer exists for them. Disbursed and Rejected must stay last and
// adjacent -- the screen draws them as a terminal pair, not a sequence.
export const APPLICATION_STAGES = [
  APPLICATION_STAGE.EC1_PENDING,
  APPLICATION_STAGE.NO_OFFER_CLICKED,
  APPLICATION_STAGE.SOME_OFFERS_CLICKED,
  APPLICATION_STAGE.ALL_OFFERS_CLICKED,
  APPLICATION_STAGE.UNDER_PROCESS,
  APPLICATION_STAGE.DISBURSED,
  APPLICATION_STAGE.REJECTED,
];

/* -------------------------------------------------------------- attribution */
// users/models.py :: CustomUser.platform
export const PLATFORM = {
  WEB: "Web",
  ANDROID: "Android",
  PARTNER: "Partner",
  WHATSAPP: "Whatsapp",
};

export const PLATFORMS = [
  PLATFORM.WEB,
  PLATFORM.ANDROID,
  PLATFORM.PARTNER,
  PLATFORM.WHATSAPP,
];


/* ----------------------------------------------------------- notifications */
// keys.py:1716-1719 -- MasterNotificationTypeData values, plus Push (FCM),
// which is modelled separately server-side via FCMNotification.
// RCS deliberately absent: it does not exist anywhere in the codebase.
export const CHANNEL = {
  PUSH: "Push",
  WHATSAPP: "Whatsapp",
  SMS: "SMS",
  EMAIL: "Email",
};

export const CHANNELS = [
  { value: CHANNEL.PUSH, label: "Push", icon: "bell" },
  { value: CHANNEL.WHATSAPP, label: "WhatsApp", icon: "message-circle" },
  { value: CHANNEL.SMS, label: "SMS", icon: "message-square" },
  { value: CHANNEL.EMAIL, label: "Email", icon: "mail" },
];

// keys.py:1722-1732 -- MasterNotificationSubStatusData values.
export const DELIVERY_STATUS = {
  SENT: "sent",
  DELIVERED: "delivered",
  READ: "read",
  LINK_CLICKED: "link_clicked",
  FAILED: "failed",
  PENDING: "pending",
};

// keys.py :: NotificationKeys -- the real WhatsApp template names.
// The inconsistent "karofin_" vs "karrofin_" spelling is intentional and
// matches production; seven of these were registered misspelt.
export const WHATSAPP_TEMPLATES = [
  { name: "karrofin_welcome", label: "Welcome" },
  { name: "karrofin_product_benefits", label: "Product Benefits" },
  { name: "karofin_ec1_completed", label: "Sign up completed" },
  { name: "karofin_rejected", label: "Rejected" },
  { name: "karrofin_loan_pre_approved", label: "Loan Pre-Approved" },
  { name: "karofin_offer_reminder", label: "Offer Reminder" },
  { name: "karofin_usp_highlight", label: "USP Highlight" },
  { name: "karofin_fomo_message", label: "FOMO Message" },
  { name: "karofin_myth_buster", label: "Myth Buster" },
  { name: "karofin_expiry_warning", label: "Expiry Warning" },
  { name: "karofin_still_available", label: "Still Available" },
  { name: "karrofin_disbursal_confirmation", label: "Disbursal Confirmation" },
  { name: "karrofin_friendly_nudge", label: "Friendly Nudge" },
  { name: "karrofin_last_chance", label: "Last Chance" },
];

// dlt_keys.py:49-52 -- the KarroFin SMS templates registered on the DLT portal.
//
// SMS is not free text. Indian SMS is DLT-regulated and MSG91 rejects any body
// that is not a registered template, so this channel gets a picker rather than
// the Title/Message boxes Push and Email use. Only two entries because only two
// are registered under the KAROFN sender -- KARROFIN_OTP is registered as well
// and is deliberately not offered, since an OTP belongs to the login flow.
//
// Adding to this list means registering the copy on the DLT portal first and
// adding its ID to dlt_keys.py; the server serves the authoritative list, this
// is the fallback for when that call fails.
export const SMS_TEMPLATES = [
  { name: "KARROFIN_WELCOME", label: "Welcome" },
  { name: "APPLICATION_REJECTED_KARROFIN", label: "Application rejected" },
];


/* ------------------------------------------------------------ user stages */

/**
 * The four stages on User Management.
 *
 * Deliberately the SAME string values the rest of the panel already uses, so
 * STATUS_COLOR keeps colouring them and DISPLAY_LABEL keeps rendering
 * "EC1 Pending" as "Sign up". A parallel vocabulary would mean a second colour
 * map and a second set of labels to keep in step.
 *
 * Unlike the lead buckets, these are MUTUALLY EXCLUSIVE -- one user, one
 * stage -- so the four tab counts add up to the total instead of overlapping.
 */
export const USER_STAGE = {
  SIGNUP: APPLICATION_STAGE.EC1_PENDING,
  OFFERS_AVAILABLE: APPLICATION_STAGE.OFFER_AVAILABLE,
  DISBURSED: APPLICATION_STAGE.DISBURSED,
  REJECTED: APPLICATION_STAGE.REJECTED,
};

export const USER_STAGES = [
  USER_STAGE.SIGNUP,
  USER_STAGE.OFFERS_AVAILABLE,
  USER_STAGE.DISBURSED,
  USER_STAGE.REJECTED,
];

/* ------------------------------------------------------- rejection reasons */
export const REJECTION_REASONS = [
  "CIBIL score below cut-off",
  "Income below minimum",
  "Pincode not serviceable",
  "Age outside permitted range",
  "Employment type not eligible",
  "Existing loan on record",
  "Bank statement not shared",
  "Document verification failed",
];

/* ----------------------------------------------------------------- colours */
// Mapped onto the Vuetify theme in plugins/vuetify.js where possible.
export const STATUS_COLOR = {
  // positive
  Disbursed: "#2E7D32",
  disbursed: "#2E7D32",
  Completed: "#2E7D32",
  Approved: "#2E7D32",
  approved: "#2E7D32",
  "Precheck Approved": "#2E7D32",
  "Approved By NBFC": "#2E7D32",
  "Card Activated": "#2E7D32",
  // Not positive and not terminal -- the card is on its way. The legacy Leads
  // screen carried its own colour map and drew this one purple; that map went
  // when the screen moved onto StatusChip, and without an entry here the status
  // would have fallen through to the grey default.
  "Card Dispatched": "#6D28D9",
  // Insurance. The server sends its own bucket colours (#B0BD8C for Converted,
  // #EFD595 for Interested) but the strip drops them so every screen's counts
  // read the same way -- which left both chips on the grey fallback. These are
  // the panel's own green and amber, in the same spirit as those two.
  Converted: "#2E7D32",
  Interested: "#B45309",
  delivered: "#2E7D32",
  read: "#1B5E20",
  sent: "#2E7D32",
  // in-flight
  "Under Process": "#B45309",
  "Pending By NBFC": "#B45309",
  Pending: "#B45309",
  "Precheck Pending": "#B45309",
  "Application Pending": "#B45309",
  "Action Required": "#C2410C",
  "Offer Generated": "#1976D2",
  "Offer Available": "#1976D2",
  "EC1 Pending": "#7B1FA2",
  "Internal EC": "#7B1FA2",
  Lead: "#0277BD",
  pending: "#B45309",
  link_clicked: "#00838F",
  // negative
  Rejected: "#C62828",
  "Rejected By NBFC": "#C62828",
  "Rejected By Cashpo": "#C62828",
  "Rejected By Karrofin": "#C62828",
  "EC Rejected": "#C62828",
  failed: "#C62828",
  // Campaign state. Without these the chips fall through to the grey default,
  // which reads the same for a live campaign as for a switched-off one.
  Active: "#2E7D32",
  Inactive: "#54636F",
  // neutral
  "No offer clicked": "#54636F",
  "Some offers clicked": "#0277BD",
  "All offers clicked": "#00796B",
};

/* ------------------------------------------------------- template library */

/**
 * A template is Active or Inactive and nothing else (since 23 Oct 2026):
 * active when it is switched on and has everything it needs to work, inactive
 * otherwise, with the reasons (`inactive_reasons`). Drives the filter and badge.
 */
export const TEMPLATE_STATUSES = ["active", "inactive"];

/**
 * Colours for those two states. Not in STATUS_COLOR, whose keys are loan and
 * lead statuses; passed to StatusChip explicitly instead.
 */
export const TEMPLATE_STATUS_COLOR = {
  active: "#2E7D32",
  inactive: "#78909C",
};

/** What each state means, for the filter dropdown. */
export const TEMPLATE_STATUS_HELP = {
  active: "The growth agent may pick it.",
  inactive: "Not sent: turned off, or something it needs is missing (see why on the row).",
};

/** Journey stages a template can be tagged for. Must match AutomationRule.Stage. */
export const TEMPLATE_STAGES = [
  "signup",
  "profile_pending",
  "offer_page",
  "offer_clicked",
  "disbursed",
  "rejected",
  "offer_expired",
  "after_one_disbursal",
];

/**
 * What a message is FOR. The agent filters the library on this, so it is a
 * controlled vocabulary rather than free text -- two spellings of "reminder"
 * would split one intent into two the model has to choose between.
 */
export const TEMPLATE_INTENTS = [
  "welcome",
  "profile_reminder",
  "offer_reminder",
  "offer_nudge",
  "offer_expiry",
  "disbursal_confirmation",
  "rejection",
  "cross_sell",
  // GrowMyScore copy is filed under this; without it here, editing one showed
  // an empty intent.
  "growth",
  "other",
];

/**
 * Where the Agent Tracker leaves a suggested template before opening the
 * Template Library with ?draft=1, which starts its "New template" form from it.
 * Session storage, not the URL: a drafted email body does not fit in a query.
 */
export const TEMPLATE_DRAFT_KEY = "kf_template_draft";

/*
 * The growth agent's tags on a template (notification_keys.GROWTH_PURPOSES,
 * GROWTH_TONES and the borrower types in intelligence/persona.py). The server
 * rejects any other value, so these lists must match it.
 */
export const TEMPLATE_PURPOSES = [
  { value: "complete_details", label: "Finish your details" },
  { value: "click_offers", label: "Look at your offers" },
  { value: "offer_benefits", label: "What your offers can do" },
  { value: "suggest_offers", label: "Offers that suit you" },
  { value: "inactivity_checkin", label: "Friendly check-in" },
  { value: "proceed_loan", label: "Finish your loan in progress" },
  { value: "more_disbursals", label: "Take another loan" },
  { value: "gms", label: "GrowMyScore" },
  { value: "revisit_3_months", label: "Come back for fresh offers" },
];

export const TEMPLATE_TONES = [
  { value: "friendly", label: "Friendly" },
  { value: "checkin", label: "Check-in" },
  { value: "benefit", label: "Benefit" },
  { value: "motivating", label: "Motivating" },
  { value: "urgency", label: "Urgency (keen borrowers only)" },
];

export const BORROWER_TYPES = [
  { value: "new_user", label: "New user (profile incomplete)" },
  { value: "offers_not_clicked", label: "Offers not clicked" },
  { value: "some_clicked_no_disbursal", label: "Clicked some, no loan yet" },
  { value: "some_clicked_under_process", label: "Clicked some, loan in process" },
  { value: "some_clicked_with_disbursal", label: "Clicked some, got a loan" },
  { value: "all_clicked_no_disbursal", label: "Clicked all, no loan" },
  { value: "all_clicked_under_process", label: "Clicked all, loan in process" },
  { value: "all_clicked_with_disbursal", label: "Clicked all, got a loan" },
  { value: "no_offers", label: "No offers" },
];

export const DEFAULT_STATUS_COLOR = "#546E7A";

/* ------------------------------------------------------------------ labels */

/**
 * Display-only renames.
 *
 * The keys here are VALUES -- they are what the data carries, what gets
 * compared against, what the colour map is keyed on, and what a drill-down
 * link puts in the query string. None of that may change. This map only
 * affects how a value READS on screen.
 *
 * "EC1 Pending" is the panel's own shorthand for a user who signed up but has
 * not finished the eligibility check. "Sign up" says the same thing without
 * the internal jargon, so the label changes and the value does not.
 *
 * Same technique as the " By NBFC" suffix strip in StatusChip.vue.
 */
export const DISPLAY_LABEL = {
  "EC1 Pending": "Sign up",
  "EC1 pending": "Sign up",
  "EC1 Completed": "Sign up completed",
};

/** The on-screen form of a value. Anything unmapped passes through. */
export function displayLabel(value) {
  if (value === null || value === undefined) return value;
  return DISPLAY_LABEL[value] || value;
}
